import InvestorPortalLayout from '@/layouts/InvestorPortalLayout'

interface Participation {
  id: number
  loan_id_anonymized: string
  loan_status: string
  principal_amount: number
  ownership_percent: number
  interest_received: number
  principal_returned: number
  total_received: number
  start_date: string
  term_months: number
  annual_rate: number
  lender_business_name: string
}

interface Props {
  participations: Participation[]
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const STATUS_BADGES: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  paid_off: { label: 'Paid Off', bg: 'bg-gray-100', text: 'text-gray-600' },
  defaulted: { label: 'Defaulted', bg: 'bg-red-50', text: 'text-red-700' },
  written_off: { label: 'Written Off', bg: 'bg-amber-50', text: 'text-amber-700' },
}

export default function Loans({ participations }: Props) {
  return (
    <InvestorPortalLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Loans</h1>
        <p className="text-sm text-gray-500 mt-1">
          Loans you have a participation in, with your principal, ownership share, and distributions to date.
        </p>
      </div>

      {participations.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <p className="text-sm text-gray-500">
            No loan participations yet. When your lender adds you to a loan, it will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {participations.map((p) => {
            const badge = STATUS_BADGES[p.loan_status] || STATUS_BADGES.active
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900">{p.loan_id_anonymized}</h3>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      via {p.lender_business_name} &middot; {p.term_months} mo @ {p.annual_rate}% &middot; started {p.start_date}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Your share</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">
                      {p.ownership_percent.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5">
                  <Stat label="Your Principal" value={fmtMoney(p.principal_amount)} />
                  <Stat label="Interest Received" value={fmtMoney(p.interest_received)} />
                  <Stat label="Principal Returned" value={fmtMoney(p.principal_returned)} />
                  <Stat label="Total Received" value={fmtMoney(p.total_received)} highlight />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-8 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
        <p className="text-xs text-gray-500">
          Borrower details are intentionally hidden. Contact your lender directly for borrower-specific information.
        </p>
      </div>
    </InvestorPortalLayout>
  )
}

function Stat({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm font-semibold font-mono mt-0.5 ${highlight ? 'text-emerald-700' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  )
}
